import { memo } from "react"
import { useLang } from "hooks/useLang"
import { IFinalEvent } from "types/event"
import DataDisplay from "./atoms/DateDisplay"
import Image from "next/image"
import truncateString from "utils/helpers/truncateString"
import {
    getLowestTicketPrice,
    hasMultiplePrices,
} from "utils/helpers/eventPrice"
import Link from "next/link"
import SAR from "./Sar/SAR"
import { useState } from "react"
import { getEventPosterUrl } from "./atoms/Image/EventPoster"

interface EventCardProps {
    event: IFinalEvent
    className?: string
    isOrgPage?: boolean
    priorityImage?: boolean
    aspectRatio?: "widescreen" | "portrait"
}
const NewEventCard = memo(function NewEventCard({
    event,
    priorityImage,
    aspectRatio = "widescreen",
    className,
}: EventCardProps) {
    const { multiLangVars, t, isAr, lang } = useLang()

    const aspectRatioClass =
        aspectRatio === "portrait" ? "aspect-[11/16]" : "aspect-[16/9]"

    const fallback = "/svg/v2/pass_ticket_fallback_image_light.png"

    const desktopImg = getEventPosterUrl(event, "desktop")
    const mobileImg = getEventPosterUrl(event, "mobile")

    const price = getLowestTicketPrice(event)

    const isMultiplePrices = hasMultiplePrices(event)

    const eventTitle =
        event?.[multiLangVars("title") as "en_title" | "ar_title"] || ""

    const eventLink = event?.slug
        ? `/events/${event?.slug}`
        : `/events/${event?._id}`

    return (
        <Link
            href={eventLink}
            className={`relative flex  md:w-full cursor-pointer flex-col gap-y-[15px] group ${className}`}
        >
            <div
                className={`relative rounded-[10px] ${aspectRatioClass}  transition-transform duration-300 lg:hover:opacity-50`}
            >
                {event?.ar_label && (
                    <div
                        className={`absolute top-0 right-0 z-10 bg-secondary-theme-400 text-white px-2 py-1 rounded-l-[1px] text-sm capitalize rounded-tr-[10px]`}
                    >
                        {isAr ? event?.ar_label : event?.en_label}
                    </div>
                )}
                <Image
                    src={desktopImg}
                    alt={eventTitle}
                    fill
                    priority={priorityImage ?? false}
                    className="absolute object-cover rounded-[10px] shadow-md shadow-slate-300 dark:shadow-none"
                    sizes="(max-width: 767px) 100vw, 50vw"
                    onError={(e) => {
                        const target = e.target as HTMLImageElement
                        target.src = fallback
                    }}
                />
                {event?.lowest_price !== undefined ||
                event?.price ||
                event?.availableTickets ? (
                    <h6
                        className={`absolute capitalize bottom-[4%] bg-black py-1.5 px-2 text-xs rounded-[3px] bg-opacity-80 ${
                            isAr ? "right-[2%]" : "left-[2%]"
                        }`}
                    >
                        {price === 0 ? (
                            <span>{t("free")}</span>
                        ) : isMultiplePrices ? (
                            <SAR
                                price={
                                    lang === "ar" ? "+" + price : price + "+"
                                }
                            />
                        ) : (
                            <SAR price={price} />
                        )}
                    </h6>
                ) : undefined}
            </div>

            <div className="data flex flex-col gap-y-[6px] px-3">
                <h3 className="font-bold text-md capitalize">
                    {truncateString(eventTitle, 40)}
                </h3>
                <div className="flex gap-x-1 text-xs font-light dark:text-white text-black">
                    <span>{t("from")}</span>
                    <DataDisplay
                        date={
                            event?.upcoming_time ??
                            event?.event_date?.starting_date ??
                            ""
                        }
                    />
                </div>
            </div>
        </Link>
    )
})

export default NewEventCard
